import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend,
} from "recharts";
import { RefreshCw, Edit3, Key } from "lucide-react";
import { api } from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";

const chartTip = { backgroundColor: "#1e2130", border: "1px solid #2a2d3a", borderRadius: "8px", color: "#e8eaf0", fontSize: "13px" };
const PRIORITY_COLORS = { critical: "#ef4444", high: "#f97316", medium: "#eab308", low: "#3b82f6", ok: "#22c55e" };
const QUALITY_COLORS = { good: "#22c55e", fair: "#eab308", poor: "#f97316", black: "#ef4444" };

function SiteDetail({ siteId, onRefresh }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [showIpInput, setShowIpInput] = useState(false);
    const [showPassInput, setShowPassInput] = useState(false);
    const [ipValue, setIpValue] = useState("");
    const [passValue, setPassValue] = useState("");
    const [actionMsg, setActionMsg] = useState(null);

    useEffect(() => {
        api.camerasForStore(siteId).then(d => { setDetail(d); setLoading(false); }).catch(() => setLoading(false));
    }, [siteId]);

    const handleAction = useCallback(async (action) => {
        setActionLoading(action);
        setActionMsg(null);
        try {
            let result;
            if (action === "recheck-nvr") {
                result = await api.recheckNvr(siteId);
                setActionMsg({ type: "ok", text: `NVR: ${result.status} — ${result.cameras_online}/${result.cameras_total} cameras` });
            } else if (action === "recheck-ptz") {
                result = await api.recheckPtz(siteId);
                setActionMsg({ type: "ok", text: `PTZ: ${result.ptzStatus} at ${result.ptzIp}` });
            } else if (action === "save-ip") {
                if (!/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ipValue)) {
                    setActionMsg({ type: "err", text: "Invalid IP address" });
                    setActionLoading(null);
                    return;
                }
                await api.updateNvrIp(siteId, ipValue);
                setShowIpInput(false);
                setActionMsg({ type: "ok", text: `IP updated to ${ipValue} — rechecking...` });
                try {
                    result = await api.recheckNvr(siteId);
                    setActionMsg({ type: "ok", text: `IP updated — NVR: ${result.status}, ${result.cameras_online}/${result.cameras_total} cameras` });
                } catch (e) {
                    setActionMsg({ type: "ok", text: `IP saved. Recheck failed: ${e.message}` });
                }
            } else if (action === "save-pass") {
                if (!passValue) {
                    setActionMsg({ type: "err", text: "Password required" });
                    setActionLoading(null);
                    return;
                }
                await api.updateNvrPassword(siteId, passValue);
                setShowPassInput(false);
                setPassValue("");
                setActionMsg({ type: "ok", text: "Password updated — rechecking..." });
                try {
                    result = await api.recheckNvr(siteId);
                    setActionMsg({ type: "ok", text: `Password updated — NVR: ${result.status}, ${result.cameras_online}/${result.cameras_total} cameras` });
                } catch (e) {
                    setActionMsg({ type: "ok", text: `Password saved. Recheck failed: ${e.message}` });
                }
            }
            // Refresh the detail panel to show updated NVR info + images
            api.camerasForStore(siteId).then(d => setDetail(d)).catch(() => {});
            if (onRefresh) onRefresh();
        } catch (err) {
            setActionMsg({ type: "err", text: err.message });
        } finally {
            setActionLoading(null);
        }
    }, [siteId, ipValue, passValue, onRefresh]);

    if (loading) return <div style={{ padding: 16, color: "var(--text-muted)" }}>Loading cameras...</div>;

    const nvr = detail?.nvr;
    const cameras = detail?.cameras || [];
    const recentImages = detail?.recentImages || [];
    const middayImages = detail?.middayImages || [];

    return (
        <div style={{ padding: "12px 24px 16px", background: "var(--bg-card)", borderTop: "1px solid var(--border)" }}>
            {nvr && (
                <>
                    <div style={{ display: "flex", gap: 24, marginBottom: 12, fontSize: 13, color: "var(--text-secondary)", flexWrap: "wrap" }}>
                        <span><strong>IP:</strong> {nvr.ip_address}</span>
                        {nvr.model && <span><strong>Model:</strong> {nvr.model}</span>}
                        {nvr.firmware && <span><strong>Firmware:</strong> {nvr.firmware}</span>}
                        {nvr.serial && <span><strong>Serial:</strong> <span style={{ fontSize: 11 }}>{nvr.serial.substring(0, 30)}{nvr.serial.length > 30 ? '...' : ''}</span></span>}
                        <span><strong>Retention:</strong> {nvr.retention_days}d</span>
                        {nvr.work_mode && <span><strong>Mode:</strong> {nvr.work_mode}</span>}
                    </div>
                    {/* HDD storage bar */}
                    {nvr.hdd_count > 0 && (
                        <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>
                                Storage: {nvr.hdd_count} HDD, {nvr.hdd_total_gb ? `${Number(nvr.hdd_total_gb).toLocaleString()} GB total` : ''} \u2014 {nvr.hdd_status}
                            </div>
                            <div style={{ height: 8, background: "var(--bg-hover)", borderRadius: 4, overflow: "hidden", maxWidth: 300 }}>
                                <div style={{
                                    height: "100%", borderRadius: 4,
                                    width: `${Math.min(nvr.hdd_used_pct || 0, 100)}%`,
                                    background: (nvr.hdd_used_pct || 0) > 90 ? "#ef4444" : (nvr.hdd_used_pct || 0) > 75 ? "#f97316" : "#22c55e",
                                }} />
                            </div>
                            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{nvr.hdd_used_pct || 0}% used</div>
                        </div>
                    )}
                    {/* Timezone/DST status */}
                    {(nvr.needs_clock || nvr.needs_dst) && (
                        <div style={{ fontSize: 12, color: "#f97316", marginBottom: 8 }}>
                            {nvr.needs_clock ? 'Clock needs correction' : ''}{nvr.needs_clock && nvr.needs_dst ? ' | ' : ''}{nvr.needs_dst ? 'DST needs update' : ''}
                        </div>
                    )}
                </>
            )}
            {/* Action buttons */}
            <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginRight: 4 }}>NVR:</span>
                <button className="preset-action-btn" disabled={!!actionLoading}
                    onClick={() => handleAction("recheck-nvr")}
                    style={{ padding: "4px 10px", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                    <RefreshCw size={12} className={actionLoading === "recheck-nvr" ? "spin" : ""} /> Recheck
                </button>
                <button className="preset-action-btn" disabled={!!actionLoading}
                    onClick={() => { setShowIpInput(!showIpInput); setShowPassInput(false); }}
                    style={{ padding: "4px 10px", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                    <Edit3 size={12} /> Enter IP
                </button>
                <button className="preset-action-btn" disabled={!!actionLoading}
                    onClick={() => { setShowPassInput(!showPassInput); setShowIpInput(false); }}
                    style={{ padding: "4px 10px", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                    <Key size={12} /> Enter Password
                </button>

                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", marginLeft: 12, marginRight: 4 }}>PTZ:</span>
                <button className="preset-action-btn" disabled={!!actionLoading}
                    onClick={() => handleAction("recheck-ptz")}
                    style={{ padding: "4px 10px", fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
                    <RefreshCw size={12} className={actionLoading === "recheck-ptz" ? "spin" : ""} /> Recheck
                </button>

                {actionMsg && (
                    <span style={{ fontSize: 12, marginLeft: 8, color: actionMsg.type === "ok" ? "#22c55e" : "#ef4444", fontWeight: 600 }}>
                        {actionMsg.text}
                    </span>
                )}
            </div>

            {/* IP input inline */}
            {showIpInput && (
                <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
                    <input type="text" placeholder={nvr?.ip_address || "e.g. 10.1.1.1"} value={ipValue}
                        onChange={e => setIpValue(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") handleAction("save-ip"); }}
                        style={{ fontSize: 13, padding: "4px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-hover)", color: "var(--text-primary)", width: 180 }} />
                    <button className="preset-action-btn" disabled={!!actionLoading}
                        onClick={() => handleAction("save-ip")}
                        style={{ padding: "4px 12px", fontSize: 12, color: "#22c55e" }}>
                        Save
                    </button>
                    <button className="preset-action-btn" onClick={() => setShowIpInput(false)}
                        style={{ padding: "4px 10px", fontSize: 12 }}>Cancel</button>
                </div>
            )}

            {/* Password input inline */}
            {showPassInput && (
                <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
                    <input type="password" placeholder="New NVR password" value={passValue}
                        onChange={e => setPassValue(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") handleAction("save-pass"); }}
                        style={{ fontSize: 13, padding: "4px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-hover)", color: "var(--text-primary)", width: 200 }} />
                    <button className="preset-action-btn" disabled={!!actionLoading}
                        onClick={() => handleAction("save-pass")}
                        style={{ padding: "4px 12px", fontSize: 12, color: "#22c55e" }}>
                        Save
                    </button>
                    <button className="preset-action-btn" onClick={() => setShowPassInput(false)}
                        style={{ padding: "4px 10px", fontSize: 12 }}>Cancel</button>
                </div>
            )}

            {cameras && cameras.length > 0 ? (
                <table className="store-table" style={{ fontSize: 12 }}>
                    <thead>
                        <tr><th>Cam</th><th>Quality</th><th>Size</th><th>Classification</th><th>Overlay</th></tr>
                    </thead>
                    <tbody>
                        {cameras.map((c, i) => (
                            <tr key={i}>
                                <td style={{ fontWeight: 600 }}>{c.cam_num}</td>
                                <td style={{ color: QUALITY_COLORS[c.quality] || "var(--text-muted)" }}>{c.quality || "\u2014"}</td>
                                <td>{c.size_kb ? `${c.size_kb}KB` : "\u2014"}</td>
                                <td>{c.classification || "\u2014"}</td>
                                <td style={{ color: "var(--text-muted)" }}>{c.overlay_name || "\u2014"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div style={{ fontSize: 13, color: "var(--text-muted)" }}>No camera snapshots for this site</div>
            )}

            {/* Recent captures from blob */}
            {recentImages && recentImages.length > 0 && (
                <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", marginBottom: 8 }}>
                        Recent Captures ({recentImages.length})
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {recentImages.map((img, i) => (
                            <div key={i} onClick={() => setSelectedImage(img)}
                                style={{ cursor: "pointer", position: "relative", borderRadius: 6, overflow: "hidden", border: "1px solid var(--border)" }}>
                                <img src={img.url} alt={img.preset}
                                    style={{ width: 140, height: 100, objectFit: "cover", display: "block" }} />
                                <div style={{
                                    position: "absolute", bottom: 0, left: 0, right: 0,
                                    background: "rgba(0,0,0,0.7)", padding: "3px 6px",
                                    fontSize: 10, color: "#fff", display: "flex", justifyContent: "space-between"
                                }}>
                                    <span style={{ fontWeight: 600 }}>{img.preset}</span>
                                    <span style={{ opacity: 0.7 }}>{img.date}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Midday captures from hourly-captures */}
            {middayImages && middayImages.length > 0 && (
                <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", marginBottom: 8 }}>
                        Midday Captures ({middayImages.length} cameras)
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {middayImages.map((img, i) => (
                            <div key={i} onClick={() => setSelectedImage(img)}
                                style={{ cursor: "pointer", position: "relative", borderRadius: 6, overflow: "hidden", border: "1px solid var(--border)" }}>
                                <img src={img.url} alt={`Camera ${img.camera}`}
                                    style={{ width: 160, height: 110, objectFit: "cover", display: "block" }} loading="lazy" />
                                <div style={{
                                    position: "absolute", bottom: 0, left: 0, right: 0,
                                    background: "rgba(0,0,0,0.7)", padding: "3px 6px",
                                    fontSize: 10, color: "#fff", display: "flex", justifyContent: "space-between"
                                }}>
                                    <span style={{ fontWeight: 600 }}>Cam {img.camera}</span>
                                    <span style={{ opacity: 0.7 }}>{img.date}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Full-size image modal */}
            {selectedImage && (
                <div onClick={() => setSelectedImage(null)}
                    style={{
                        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(0,0,0,0.85)", zIndex: 9999,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer"
                    }}>
                    <div style={{ maxWidth: "90vw", maxHeight: "90vh", position: "relative" }}>
                        <img src={selectedImage.url} alt={selectedImage.preset}
                            style={{ maxWidth: "90vw", maxHeight: "85vh", objectFit: "contain", borderRadius: 8 }} />
                        <div style={{
                            textAlign: "center", marginTop: 8, color: "#fff", fontSize: 14
                        }}>
                            <strong>{selectedImage.preset}</strong> {"\u2014"} {selectedImage.date}
                            {selectedImage.size && <span style={{ opacity: 0.6, marginLeft: 12 }}>{(selectedImage.size / 1024).toFixed(0)}KB</span>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const thStyle = { background: "var(--bg-card)", position: "sticky", top: 0, zIndex: 2 };

function SortHeader({ label, field, sortField, sortDir, onSort, style }) {
    const active = sortField === field;
    return (
        <th style={{ ...thStyle, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", ...style }} onClick={() => onSort(field)}>
            {label} <span style={{ opacity: active ? 1 : 0.3, fontSize: 10 }}>{active && sortDir === "desc" ? "\u25BC" : "\u25B2"}</span>
        </th>
    );
}

function sortRows(rows, field, dir) {
    if (!field) return rows;
    const mult = dir === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
        let va = a[field], vb = b[field];
        if (va == null) va = "";
        if (vb == null) vb = "";
        if (typeof va === "number" && typeof vb === "number") return (va - vb) * mult;
        return String(va).localeCompare(String(vb)) * mult;
    });
}

function Infrastructure() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [stateFilter, setStateFilter] = useState("all");
    const [expandedSite, setExpandedSite] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortField, setSortField] = useState(null);
    const [sortDir, setSortDir] = useState("asc");
    const [statusFilter, setStatusFilter] = useState("all");

    useEffect(() => {
        api.cameraHealth().then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    }, []);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(d => d === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDir("asc");
        }
    };

    // All hooks must be called before any early return (Rules of Hooks)
    const allStores = useMemo(() => {
        let list = data?.stores || [];
        if (stateFilter !== "all") list = list.filter(s => s.state_code === stateFilter);
        if (statusFilter === "online") list = list.filter(s => s.status === "OK");
        else if (statusFilter === "auth") list = list.filter(s => s.status === "AUTH_FAIL");
        else if (statusFilter === "unreachable") list = list.filter(s => s.status === "UNREACHABLE");
        else if (statusFilter === "no_nvr") list = list.filter(s => s.status === "NO_NVR");
        else if (statusFilter === "no_ptz") list = list.filter(s => s.pieface_status !== "OK");
        else if (statusFilter === "pieface") list = list.filter(s => s.has_pieface);
        else if (statusFilter === "pieface_no_ptz") list = list.filter(s => s.has_pieface && s.pieface_status !== "OK");
        else if (statusFilter === "issues") list = list.filter(s => s.issues && s.issues.length > 0);
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(s =>
                (s.site_id && String(s.site_id).toLowerCase().includes(term)) ||
                (s.site_name && s.site_name.toLowerCase().includes(term))
            );
        }
        return sortRows(list, sortField, sortDir);
    }, [data, stateFilter, statusFilter, searchTerm, sortField, sortDir]);

    const countByStatus = useMemo(() => {
        const s = data?.stores || [];
        return {
            all: s.length,
            online: s.filter(x => x.status === "OK").length,
            auth: s.filter(x => x.status === "AUTH_FAIL").length,
            unreachable: s.filter(x => x.status === "UNREACHABLE").length,
            no_nvr: s.filter(x => x.status === "NO_NVR").length,
            no_ptz: s.filter(x => x.pieface_status !== "OK").length,
            pieface: s.filter(x => x.has_pieface).length,
            pieface_no_ptz: s.filter(x => x.has_pieface && x.pieface_status !== "OK").length,
            issues: s.filter(x => x.issues && x.issues.length > 0).length,
        };
    }, [data]);

    if (loading) return <LoadingSpinner message="Loading infrastructure..." />;

    const t = data?.totals || {};
    const hasData = t.total_nvrs > 0;
    const byState = data?.byState || [];
    const stores = data?.stores || [];
    const quality = data?.qualityBreakdown || [];
    const scanTrend = data?.scanTrend || [];
    const modelDistribution = data?.modelDistribution || [];
    const firmwareOutdated = data?.firmwareOutdated || 0;
    const clockDrift = data?.clockDrift || 0;
    const states = [...new Set(byState.map(s => s.state_code))].sort();
    const sitesOnlinePct = t.total_nvrs > 0 ? (((t.nvrs_online || 0) / t.total_nvrs) * 100).toFixed(1) : 0;
    const nvrIssueCount = (t.nvrs_unreachable || 0) + (t.nvrs_auth_fail || 0) + (t.hdd_issues || 0) + (t.no_recording || 0);

    const stateChartData = byState.map(s => ({
        state: s.state_code,
        online: s.total_cams_online || 0,
    }));

    const sitesVsNvrData = byState.map(s => ({
        state: s.state_code,
        total_sites: s.total_sites || 0,
        nvrs_online: s.nvrs_online || 0,
        no_nvr: (s.total_sites || 0) - (s.total_nvrs || 0),
    }));

    const qualityPie = quality.filter(q => q.quality).map(q => ({ name: q.quality, value: q.count }));

    const toggleExpand = (siteId) => {
        setExpandedSite(expandedSite === siteId ? null : siteId);
    };

    return (
        <div>
            <div className="page-header">
                <h2>Infrastructure</h2>
                <p>NVR & camera status across {t.total_nvrs || 0} audited sites</p>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-label">Sites Audited</div>
                    <div className="stat-value">{t.total_nvrs || 0}</div>
                    <div className="stat-change neutral">of {t.total_sites || 0} network</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Total Online</div>
                    <div className="stat-value">{(t.total_cams_online || 0).toLocaleString()}</div>
                    <div className="stat-change neutral">connected cameras</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Sites Online</div>
                    <div className="stat-value" style={{ color: "#22c55e" }}>{sitesOnlinePct}%</div>
                    <div className="stat-change positive">{t.nvrs_online || 0} of {t.total_nvrs || 0} sites</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">NVR Issues</div>
                    <div className="stat-value" style={{ color: "#f97316" }}>{nvrIssueCount}</div>
                    <div className="stat-change neutral">{t.low_retention || 0} low retention</div>
                </div>
                {modelDistribution.length > 0 && (
                    <div className="stat-card">
                        <div className="stat-label">NVR Models</div>
                        <div className="stat-value">{modelDistribution.length}</div>
                        <div className="stat-change neutral">{modelDistribution[0]?.model || 'N/A'} most common</div>
                    </div>
                )}
                {firmwareOutdated > 0 && (
                    <div className="stat-card">
                        <div className="stat-label">Firmware Outdated</div>
                        <div className="stat-value" style={{ color: "#f97316" }}>{firmwareOutdated}</div>
                        <div className="stat-change neutral">below V4.60</div>
                    </div>
                )}
                {clockDrift > 0 && (
                    <div className="stat-card">
                        <div className="stat-label">Clock Drift</div>
                        <div className="stat-value" style={{ color: "#eab308" }}>{clockDrift}</div>
                        <div className="stat-change neutral">NVRs need correction</div>
                    </div>
                )}
            </div>

            {!hasData && (
                <div className="card" style={{ padding: 40, textAlign: "center" }}>
                    <h3 style={{ color: "var(--text-secondary)" }}>No infrastructure data yet</h3>
                    <p style={{ color: "var(--text-muted)" }}>Run <code>seed_infrastructure.sql</code> in SSMS to populate NVR and camera data.</p>
                </div>
            )}

            {hasData && (
                <>
                    <div className="grid-2">
                        <div className="card">
                            <div className="card-title">Cameras by State</div>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={stateChartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                                    <XAxis dataKey="state" stroke="#5c6078" fontSize={12} />
                                    <YAxis stroke="#5c6078" fontSize={12} />
                                    <Tooltip contentStyle={chartTip} />
                                    <Bar dataKey="online" fill="#22c55e" name="Online" barSize={24} radius={[4,4,0,0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="card">
                            <div className="card-title">Sites vs Working NVR</div>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={sitesVsNvrData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                                    <XAxis dataKey="state" stroke="#5c6078" fontSize={12} />
                                    <YAxis stroke="#5c6078" fontSize={12} />
                                    <Tooltip contentStyle={chartTip} />
                                    <Bar dataKey="nvrs_online" fill="#22c55e" name="NVRs Online" stackId="a" barSize={28} />
                                    <Bar dataKey="no_nvr" fill="#ef4444" name="No NVR" stackId="a" barSize={28} radius={[4,4,0,0]} />
                                    <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 12, color: "#8b8fa3" }} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="grid-2" style={{ marginTop: 24 }}>
                        {qualityPie.length > 0 && (
                            <div className="card">
                                <div className="card-title">Image Quality ({quality.reduce((a,q) => a + q.count, 0).toLocaleString()} cameras)</div>
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie data={qualityPie} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                                            dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                                            {qualityPie.map(e => <Cell key={e.name} fill={QUALITY_COLORS[e.name] || "#5c6078"} />)}
                                        </Pie>
                                        <Tooltip contentStyle={chartTip} />
                                        <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 12, color: "#8b8fa3" }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                        <div className="card">
                            <div className="card-title">State Coverage</div>
                            <table className="store-table">
                                <thead>
                                    <tr>
                                        <th>State</th>
                                        <th>Sites</th>
                                        <th>NVRs</th>
                                        <th>Connectable</th>
                                        <th>PTZ Cams</th>
                                        <th>Coverage %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {byState.map(s => {
                                        const coverage = s.total_sites > 0 ? ((s.sites_with_cameras || 0) / s.total_sites * 100).toFixed(0) : 0;
                                        return (
                                            <tr key={s.state_code}>
                                                <td style={{ fontWeight: 600 }}>{s.state_code}</td>
                                                <td>{s.total_sites || 0}</td>
                                                <td>{s.total_nvrs}</td>
                                                <td style={{ color: "#3b82f6" }}>{s.sites_with_cameras || 0}</td>
                                                <td style={{ color: "#22c55e" }}>{s.ptz_ok || 0}</td>
                                                <td style={{ color: coverage >= 80 ? "#22c55e" : coverage >= 50 ? "#eab308" : "#ef4444" }}>{coverage}%</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Scan trend */}
                    {scanTrend.length > 0 && (
                        <div className="card" style={{ marginTop: 24 }}>
                            <div className="card-title">Scan Cycles (7 Days)</div>
                            <table className="store-table">
                                <thead>
                                    <tr><th>Date</th><th>Cycles</th><th>Success</th><th>Partial</th><th>Failed</th><th>Captures</th></tr>
                                </thead>
                                <tbody>
                                    {scanTrend.map(d => (
                                        <tr key={d.scan_date}>
                                            <td style={{ fontWeight: 600 }}>{new Date(d.scan_date).toLocaleDateString()}</td>
                                            <td>{d.total_cycles}</td>
                                            <td style={{ color: "#22c55e" }}>{d.successful}</td>
                                            <td style={{ color: "#f97316" }}>{d.partial}</td>
                                            <td style={{ color: d.failed > 0 ? "#ef4444" : "var(--text-muted)" }}>{d.failed}</td>
                                            <td>{d.total_captures}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* All sites with filter buttons */}
                    <div className="card" style={{ marginTop: 24 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                {[
                                    { key: "all", label: "All", count: countByStatus.all, color: "#8b8fa3" },
                                    { key: "online", label: "Online", count: countByStatus.online, color: "#22c55e" },
                                    { key: "auth", label: "Auth Fail", count: countByStatus.auth, color: "#ef4444" },
                                    { key: "unreachable", label: "Unreachable", count: countByStatus.unreachable, color: "#ef4444" },
                                    { key: "no_nvr", label: "No NVR", count: countByStatus.no_nvr, color: "#8b8fa3" },
                                    { key: "no_ptz", label: "No PTZ", count: countByStatus.no_ptz, color: "#f97316" },
                                    { key: "pieface", label: "Pie Face", count: countByStatus.pieface, color: "#a855f7" },
                                    { key: "pieface_no_ptz", label: "Pie Face / No PTZ", count: countByStatus.pieface_no_ptz, color: "#ef4444" },
                                    { key: "issues", label: "Issues", count: countByStatus.issues, color: "#f97316" },
                                ].map(f => (
                                    <button key={f.key} onClick={() => setStatusFilter(f.key)}
                                        style={{
                                            fontSize: 12, padding: "4px 10px", borderRadius: 12, cursor: "pointer",
                                            border: statusFilter === f.key ? `2px solid ${f.color}` : "1px solid var(--border)",
                                            background: statusFilter === f.key ? `${f.color}22` : "var(--bg-hover)",
                                            color: statusFilter === f.key ? f.color : "var(--text-secondary)",
                                            fontWeight: statusFilter === f.key ? 600 : 400,
                                        }}>
                                        {f.label} ({f.count})
                                    </button>
                                ))}
                            </div>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <select className="filter-select" value={stateFilter} onChange={e => setStateFilter(e.target.value)} style={{ fontSize: 13 }}>
                                    <option value="all">All States</option>
                                    {states.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <input type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                    style={{ fontSize: 13, padding: "4px 10px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--bg-hover)", color: "var(--text-primary)", width: 180 }} />
                            </div>
                        </div>
                        <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 8 }}>
                            Showing {allStores.length} of {stores.length} sites
                        </div>
                        <div style={{ maxHeight: 600, overflowY: "auto" }}>
                            <table className="store-table">
                                <thead>
                                    <tr>
                                        <SortHeader label="Site ID" field="site_id" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                                        <SortHeader label="Site Name" field="site_name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                                        <SortHeader label="State" field="state_code" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                                        <SortHeader label="NVR Status" field="status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                                        <SortHeader label="Model" field="model" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                                        <SortHeader label="Connected" field="cameras_online" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                                        <SortHeader label="Disk" field="hdd_used_pct" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                                        <SortHeader label="Retention" field="retention_days" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                                        <SortHeader label="PTZ" field="pieface_status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                                        <th style={thStyle}>Issues</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {allStores.map(s => (
                                        <React.Fragment key={s.site_id}>
                                            <tr onClick={() => toggleExpand(s.site_id)} style={{ cursor: "pointer" }}>
                                                <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                                                    <span style={{ marginRight: 6, fontSize: 10, color: "var(--text-muted)" }}>
                                                        {expandedSite === s.site_id ? "\u25BC" : "\u25B6"}
                                                    </span>
                                                    {s.site_id}
                                                </td>
                                                <td>{s.site_name}</td>
                                                <td>{s.state_code}</td>
                                                <td style={{ color: s.status === "NO_NVR" ? "var(--text-muted)" : s.status !== "OK" ? "#ef4444" : "#22c55e", fontWeight: 600, fontSize: 12 }}>{s.status}</td>
                                                <td style={{ fontSize: 11, color: "var(--text-secondary)", maxWidth: 120 }}>
                                                    {s.model ? <span title={`${s.model}${s.firmware ? ` / ${s.firmware}` : ''}${s.serial ? ` / S/N: ${s.serial}` : ''}`}>{s.model}</span> : <span style={{ color: "var(--text-muted)" }}>{"\u2014"}</span>}
                                                </td>
                                                <td style={{ color: s.cameras_online > 0 ? "#22c55e" : "var(--text-muted)" }}>{s.cameras_online} / {s.cameras_total}</td>
                                                <td style={{ fontSize: 11 }}>
                                                    {s.hdd_total_gb ? (
                                                        <span title={`${s.hdd_count} HDD, ${s.hdd_total_gb}GB total, ${s.hdd_free_gb}GB free`} style={{ color: (s.hdd_used_pct || 0) > 90 ? "#ef4444" : (s.hdd_used_pct || 0) > 75 ? "#f97316" : "#22c55e" }}>
                                                            {s.hdd_used_pct || 0}%
                                                        </span>
                                                    ) : <span style={{ color: "var(--text-muted)" }}>{"\u2014"}</span>}
                                                </td>
                                                <td style={{ color: s.retention_days != null && s.retention_days < 14 ? "#f97316" : "var(--text-muted)" }}>{s.retention_days != null ? `${s.retention_days}d` : "\u2014"}</td>
                                                <td style={{
                                                    color: s.pieface_status === "OK" ? "#22c55e" : s.pieface_status === "AUTH_FAIL" ? "#ef4444" : s.pieface_status === "UNREACHABLE" ? "#ef4444" : "var(--text-muted)",
                                                    fontWeight: 600, fontSize: 12
                                                }}>
                                                    {s.pieface_status || "\u2014"}
                                                </td>
                                                <td style={{ fontSize: 12, color: "var(--text-secondary)", maxWidth: 200 }}>
                                                    {(s.issues || "\u2014").replace(/;/g, ", ")}
                                                </td>
                                            </tr>
                                            {expandedSite === s.site_id && (
                                                <tr><td colSpan={10} style={{ padding: 0 }}>
                                                    <SiteDetail siteId={s.site_id} onRefresh={() => api.cameraHealth().then(d => setData(d))} />
                                                </td></tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default Infrastructure;

